import moment from 'moment/moment';
import { Constants } from '../common/constants';

export function getFiscalWeekStartDateList(
  startDate: string,
  numberOfWeeks: number
): string[] {
  const fiscalWeekStartDateList: string[] = [];
  for (let i = 0; i < numberOfWeeks; i++) {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() + i * 7);
    newDate.setHours(0, 0, 0, 0);
    newDate.setMinutes(-newDate.getTimezoneOffset());
    fiscalWeekStartDateList.push(
      moment(newDate).utc().format(Constants.DATE_FORMAT)
    );
  }
  return fiscalWeekStartDateList;
}
/**
 * Generates a list of daily grain dates based on the user's work preferences
 * @param startDate - The start date string
 * @param numberOfWeeks - Number of weeks to generate dates for
 * @param workingDays - Optional array of working day names (e.g., ['Monday', 'Tuesday', ...])
 *                     If not provided, defaults to Monday-Friday
 * @returns Array of date strings in DATE_FORMAT for working days only
 */
export function getDailyGrainDateList(
  startDate: string,
  numberOfWeeks: number,
  workingDays?: string[]
): string[] {
  const dailyGrainDateList: string[] = [];
  let currentDate = moment.utc(startDate).startOf('day');
  
  // Default to Monday-Friday if no working days provided
  const defaultWorkingDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const userWorkingDays = workingDays && workingDays.length > 0 ? workingDays : defaultWorkingDays;
  
  const targetNumberOfDays = numberOfWeeks * userWorkingDays.length; // Working days per week

  while (dailyGrainDateList.length < targetNumberOfDays) {
    const dayName = currentDate.format('dddd'); // Full day name (e.g., 'Monday')
    if (userWorkingDays.includes(dayName)) {
      dailyGrainDateList.push(currentDate.format(Constants.DATE_FORMAT));
    }
    currentDate.add(1, 'days');
  }
  return dailyGrainDateList;
}

export function convertDateToWeekRange(date: Date): string {
  var startOfWeek = moment(date);
  var endOfWeek = startOfWeek.clone().add(6, 'days');
  return startOfWeek.format('DD MMM') + ' - ' + endOfWeek.format('DD MMM');
}

export class TimeBlock {
  start: Date;
  end: Date;

  constructor(start: string | Date, end: string | Date) {
    this.start = new Date(start);
    this.end = new Date(end);
    if (this.start > this.end) {
      throw new Error("Start time must be before end time.");
    }
  }

  get duration(): number {
    return this.end.getTime() - this.start.getTime(); // Duration in milliseconds
  }

  overlapsWith(other: TimeBlock): boolean {
    return this.start < other.end && this.end > other.start;
  }
}

export function getHoursBetween(start: string, end: string) {
  return (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60);
}

export function isSameDay(d1: string, d2: string) {
  return new Date(d1).getFullYear() === new Date(d2).getFullYear() &&
    new Date(d1).getMonth() === new Date(d2).getMonth() &&
    new Date(d1).getDate() === new Date(d2).getDate();
}

export function MergeBlocks(blocks: TimeBlock[]): TimeBlock[] {
  if (!blocks || blocks.length === 0) return [];

  // Sort blocks by start time
  const sorted = [...blocks].sort((a, b) => a.start.getTime() - b.start.getTime());
  const merged: TimeBlock[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];

    // Overlap or adjacent (end == start)
    if (last.overlapsWith(current) || last.end.getTime() === current.start.getTime()) {
      merged[merged.length - 1] = new TimeBlock(
        last.start,
        last.end > current.end ? last.end : current.end
      );
    } else {
      merged.push(current);
    }
  }

  return merged;
}
